/**
 * ProfessionalCard Component
 * Displays a professional card with rating, contact info, and profile link
 */

import { Avatar, AvatarFallback, AvatarImage, AvatarLoading } from '@shared/components/ui/avatar'
import { Card } from '@shared/components/ui/card'
import { StarRating } from '@shared/components/ui/star-rating'
import { getInitials } from '@shared/lib/utils'
import type { Professional } from '@shared/types'
import { MapPin } from 'lucide-react'
import { useState } from 'react'

// ============================================================================
// Types
// ============================================================================

interface ProfessionalCardProps {
  professional: Professional
  onClick?: (professional: Professional) => void
  'data-tour'?: string
}

// ============================================================================
// Component
// ============================================================================

export function ProfessionalCard({
  professional,
  onClick,
  'data-tour': dataTour
}: ProfessionalCardProps) {
  const [imageLoading, setImageLoading] = useState(!!professional.profilePictureUrl)
  const [imageError, setImageError] = useState(false)

  const handleImageLoad = () => {
    setImageLoading(false)
    setImageError(false)
  }

  const handleImageError = () => {
    setImageLoading(false)
    setImageError(true)
  }

  const handleCardClick = () => {
    onClick?.(professional)
  }

  const showImage = !!professional.profilePictureUrl && !imageError
  const isLoading = imageLoading && showImage

  return (
    <Card
      className="overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
      onClick={handleCardClick}
      data-tour={dataTour}
    >
      <div className="p-6 space-y-4">
        {/* Header with Avatar and Name */}
        <div className="flex items-start gap-4">
          <Avatar className="h-16 w-16 ring-2 ring-primary/10">
            <AvatarImage
              src={professional.profilePictureUrl}
              alt={professional.professionalName}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
            <AvatarLoading isLoading={isLoading} />
            <AvatarFallback className="text-lg font-semibold bg-primary/10 text-primary">
              {getInitials(professional.professionalName)}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold truncate group-hover:text-primary transition-colors">
              {professional.professionalName}
            </h3>
            {professional.profession && (
              <p className="text-sm text-muted-foreground truncate">
                {professional.profession.name}
              </p>
            )}
          </div>
        </div>

        {/* Location */}
        {professional.city && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{professional.city.name}</span>
          </div>
        )}

        {/* Rating and Reviews */}
        <div className="flex items-center justify-between">
          <StarRating rating={professional.averageRating} size="sm" />
          <span className="text-xs text-muted-foreground">
            {professional.totalReviews === 0 && 'Sin reseñas'}
            {professional.totalReviews === 1 && '1 reseña'}
            {professional.totalReviews > 1 && `${professional.totalReviews} reseñas`}
          </span>
        </div>

        {/* Bio Preview */}
        {professional.bio && (
          <p className="text-sm text-muted-foreground line-clamp-2">{professional.bio}</p>
        )}

        {/* Contact Button */}
        {/* <div className="pt-2 border-t">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={handleContactClick}
            disabled={disabled}
          >
            <Phone className="h-4 w-4 mr-2" />
            Contactar
          </Button>
        </div> */}
      </div>
    </Card>
  )
}

// ============================================================================
// Loading Skeleton
// ============================================================================

export function ProfessionalCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <div className="p-6 space-y-4 animate-pulse">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="h-16 w-16 bg-muted rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="h-5 bg-muted rounded w-3/4" />
            <div className="h-4 bg-muted rounded w-1/2" />
          </div>
        </div>

        {/* Location */}
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 bg-muted rounded" />
          <div className="h-4 bg-muted rounded w-1/3" />
        </div>

        {/* Rating */}
        <div className="flex items-center justify-between">
          <div className="h-4 bg-muted rounded w-1/4" />
          <div className="h-3 bg-muted rounded w-1/4" />
        </div>

        {/* Bio */}
        <div className="space-y-2">
          <div className="h-3 bg-muted rounded w-full" />
          <div className="h-3 bg-muted rounded w-4/5" />
        </div>

        {/* Button */}
        <div className="pt-2 border-t">
          <div className="h-9 bg-muted rounded w-full" />
        </div>
      </div>
    </Card>
  )
}
